import React, {
  useState,
  useEffect,
  useCallback,
  FunctionComponent,
  ReactElement,
} from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';

interface Props {
  onConfirmAsync?: (args?: any) => Promise<any>,
  onConfirm?: (args?: any) => any,
  onClose: (args?: any) => void | Promise<any>,
  onSuccess?: (args?: any) => void,
  title?: string,
  message: string | ReactElement,
  ModalForm?: React.FC<any>,
  isSimple?: boolean,
  modalFormState?: object,
  show: boolean,
  setShow: (show: boolean) => void,
  clearBulkSelected?: () => void,
  AuxiliarySimpleButton?: FunctionComponent,
  onConfirmButtonText?: string,
  useSwal?: boolean,
  actionType?: string,
}

const ConfirmationModal: React.FC<Props> = ({
  show,
  setShow,
  onConfirmAsync,
  onConfirm,
  onClose,
  onSuccess,
  title,
  message,
  ModalForm,
  modalFormState,
  isSimple,
  clearBulkSelected,
  AuxiliarySimpleButton,
  onConfirmButtonText,
  useSwal,
  actionType,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!show) {
      if (successMessage) {
        setSuccessMessage('');
      }
    }
  }, [show, successMessage]);

  useEffect(() => {
    if (!message) {
      setShow(false);
    }
  }, [message]);

  const closeModal = useCallback(() => {
    if (successMessage) {
      if (clearBulkSelected) {
        clearBulkSelected();
      }
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } else {
      onClose();
    }
  }, [onClose, successMessage, onSuccess, clearBulkSelected]);

  return (
    <Modal
      show={show}
      onHide={closeModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title || 'Confirm Bulk Action'}</Modal.Title>
      </Modal.Header>
      {
        successMessage
          ? (
            <>
              <Modal.Body>{successMessage}</Modal.Body>
              <Modal.Footer>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (clearBulkSelected) {
                      clearBulkSelected();
                    }
                    if (onSuccess) {
                      onSuccess();
                    }
                  }}
                >
                  {onConfirmButtonText || 'OK'}
                </Button>
              </Modal.Footer>
            </>
          )
          : (
            <>
              <Modal.Body>
                <div className="confirmation-modal-message">{message}</div>
                {!!ModalForm && <ModalForm formState={modalFormState} chosenAction={actionType} />}
              </Modal.Body>
              <Modal.Footer
                className={(isSimple && AuxiliarySimpleButton) ? 'd-flex justify-content-between' : ''}
              >
                {
                  isSimple
                    ? (
                      <>
                        {AuxiliarySimpleButton && <AuxiliarySimpleButton />}
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (onConfirm) {
                              onConfirm();
                            }
                          }}
                        >
                          {onConfirmButtonText || 'OK'}
                        </Button>
                      </>
                    )
                    : (
                      <>
                        <Button
                          variant="secondary"
                          disabled={isLoading}
                          onClick={closeModal}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          disabled={isLoading}
                          onClick={() => {
                            if (onConfirmAsync) {
                              setIsLoading(true);
                              onConfirmAsync(modalFormState || null)
                                .then((res: string) => {
                                  if (useSwal) {
                                    if (onClose) {
                                      onClose();
                                    }
                                    Swal.fire(
                                      'Success',
                                      res,
                                      'success',
                                    )
                                      .then(() => {
                                        if (clearBulkSelected) {
                                          clearBulkSelected();
                                        }
                                        if (onSuccess) {
                                          onSuccess();
                                        }
                                      });
                                  } else {
                                    setSuccessMessage(`Success: ${res}`);
                                    setIsLoading(false);
                                  }
                                })
                                .catch((e: any) => {
                                  let errorMessage = 'Something went wrong, please try again';

                                  if (typeof e === 'string') {
                                    errorMessage = e;
                                  }

                                  if (e?.response?.data?.message) {
                                    errorMessage = e?.response?.data?.message;
                                  }

                                  setIsLoading(false);

                                  Swal.fire(
                                    'Error',
                                    `Error: ${errorMessage}`,
                                    'error',
                                  );
                                });
                            } else if (onConfirm) {
                              onConfirm();
                            }
                          }}
                        >
                          {
                            isLoading
                              ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  <span className="sr-only">Loading...</span>
                                </>
                              )
                              : (<>Yes</>)
                          }
                        </Button>
                      </>
                    )
                }
              </Modal.Footer>
            </>
          )
      }
    </Modal>
  );
};

ConfirmationModal.defaultProps = {
  useSwal: true,
};

export default ConfirmationModal;
